import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * OKS-675 — Fixed bottom "Join {brand}'s crew" bar for a logged-IN
 * recognised member (same account as the invited creator).
 *
 * Mirrors `<FloatingCta>`'s layout so the welcome composition looks
 * identical to the brand-new-creator flow, but the primary action is a
 * ONE-TAP join (no TikTok re-auth): a native top-level `<form method=post>`
 * POSTing to `accept_url` (`/i/:token/accept`) which attaches the new
 * `CrewMembership` via `AcceptInviteService` and 303s into the OKS-458
 * welcome arrival. NOT an Inertia `<Form>` — the server 303s to a
 * membership-scoped path that needs a top-level browser nav for cookie
 * carry-over (same rationale as the OAuth-start + logout forms).
 *
 * A secondary "Not you? Log out" affordance (auth-zero `DELETE
 * /sessions/:id` via `_method` override) renders only when `sign_out_url`
 * is present — the wrong-account escape hatch (UX Spec §2).
 *
 * NO email is rendered anywhere (privacy P0) — personalisation lives in the
 * welcome cover's greeting (`candidate_name`).
 */
export interface RecognisedJoinBarProps {
  /** POST action for the one-tap join (`accept_url` → `/i/:token/accept`). */
  accept_url: string
  /** Inviting brand's display name, interpolated into the CTA label. */
  brand_name: string
  /** Auth-zero `DELETE /sessions/:id` URL for the secondary "Not you? Log
   *  out" link. Omitted when null (session id unavailable). */
  sign_out_url: string | null
  testId?: string
}

export function RecognisedJoinBar({
  accept_url,
  brand_name,
  sign_out_url,
  testId = "invite-landing-recognised-join-bar",
}: RecognisedJoinBarProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid={testId}
      className={cn(
        "bg-background/95 fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur",
        "shadow-[0_-3px_12px_rgba(0,0,0,0.08)]",
      )}
    >
      <div className="mx-auto flex w-full flex-col gap-2 px-5 py-4 sm:max-w-md">
        <form
          method="post"
          action={accept_url}
          data-testid="invite-landing-recognised-join-form"
          className="contents"
        >
          <CsrfHiddenInput />
          <Button
            type="submit"
            size="lg"
            variant="default"
            className="min-h-[44px] w-full"
            data-testid="invite-landing-recognised-join"
          >
            {t("invites.landing.error.recognised.join_cta", {
              brand: brand_name,
            })}
          </Button>
        </form>
        {sign_out_url && (
          <form
            method="post"
            action={sign_out_url}
            data-testid="invite-landing-recognised-logout"
            className="contents"
          >
            {/*
              Auth-zero session destroy is DELETE /sessions/:id; browsers
              don't speak DELETE on form submit, so Rack::MethodOverride
              reads the `_method` body field. A bare <a href> GET would
              no-op and strand the user (same wire format as the wrong-
              account + OAuth-start forms).
            */}
            <input type="hidden" name="_method" value="delete" />
            <CsrfHiddenInput />
            <button
              type="submit"
              data-testid="invite-landing-recognised-logout-submit"
              className="text-muted-foreground hover:text-foreground self-center bg-transparent text-sm underline-offset-4 hover:underline"
            >
              {t("invites.landing.error.recognised.logout")}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function CsrfHiddenInput() {
  // Reads <meta name="csrf-token"> at render time. Returns nothing on
  // surfaces where the meta isn't in the DOM (isolated tests) — the form
  // then fails server-side CSRF, which is the correct behaviour.
  if (typeof document === "undefined") return null
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )
  if (!meta?.content) return null
  return <input type="hidden" name="authenticity_token" value={meta.content} />
}

export default RecognisedJoinBar
